
import { Modal } from 'bootstrap';
import { defineComponent, ref, inject, computed, watchEffect } from 'vue';
import { Apollo, Notify } from '@/core/services';
import { GET_PRODUCT_GROUP_PRODUCTS, GET_PRODUCT_GROUP_CATEGORIES } from '@/modules/catalog/product-groups/graphql/Queries';
import { UPDATE_GROUP_PRODUCTS } from '@/modules/catalog/product-groups/graphql/Mutations';
import { EditProductGroupForm } from '@/modules/catalog/product-groups/interfaces';
import { useI18n } from 'vue-i18n';
import InnerLoader from '@/components/InnerLoader.vue';
import { CUP_AGREEMENT_PRODUCT } from '@/modules/catalog/products/graphql/Queries';
import { GET_CATEGORIES_DROPDOWN_LIST } from '@/modules/catalog/categories/graphql/Queries';

export default defineComponent({
    name: 'edit customer group',
    components: {
        InnerLoader
    },

    setup() {
        const loader = ref(false);
        const products = ref([]) as Record<any, any>;
        const categories = ref([]) as Record<any, any>;
        const modal: any = ref();
        const loading = ref(false);
        const emitter: any = inject('emitter');
        const current_page = ref(1);
        const i18n = useI18n();
        const system_locale = ref();
        const editProductGroupForm = ref<null | HTMLFormElement>(null);
        const group_products = ref([]) as Record<any, any>;
        const product_group_id = ref();
        const assignedProductsData = ref([]) as Record<any, any>;
        const assignedCategoriesData = ref([]) as Record<any, any>;
        const assignedProducts = ref([]) as Record<any, any>;
        const assignedCategories = ref([]) as Record<any, any>;
        const assignedProduct = ref([]) as Record<any, any>;
        const productSearch = ref('');
        const categorySearch = ref('');

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
        });

        // Given Edit Customer Group Form Interface
        const editProductGroupData = ref<EditProductGroupForm>({
            id: null,
            name: '',
            uuid: ''
        });

        const getProductLabel = (name: string) => {
            try {
                const parsedName = JSON.parse(name);
                const locale = system_locale.value || i18n.fallbackLocale.value;
                return parsedName[locale]?.name || parsedName.en?.name || '';
            } catch {
                return name || '';
            }
        };

        const filteredProducts = computed(() => {
            const query = productSearch.value.trim().toLowerCase();
            if (!query) {
                return products.value;
            }
            return products.value.filter((product: Record<any, any>) => String(product.label || '').toLowerCase().includes(query));
        });

        const filteredProductIds = computed(() => filteredProducts.value.map((product: Record<any, any>) => product.value));

        const isAllFilteredSelected = computed(() => {
            const ids = filteredProductIds.value;
            if (!ids.length) {
                return false;
            }
            return ids.every((id: number) => assignedProducts.value.includes(id));
        });

        const isFilteredIndeterminate = computed(() => {
            const ids = filteredProductIds.value;
            if (!ids.length) {
                return false;
            }
            const selectedCount = ids.filter((id: number) => assignedProducts.value.includes(id)).length;
            return selectedCount > 0 && selectedCount < ids.length;
        });

        const toggleSelectAllFiltered = (checked: boolean | Event) => {
            const shouldSelect = typeof checked === 'boolean' ? checked : !isAllFilteredSelected.value;
            const ids = filteredProductIds.value;
            if (shouldSelect) {
                assignedProducts.value = Array.from(new Set([...assignedProducts.value, ...ids]));
                return;
            }
            const removeIds = new Set(ids);
            assignedProducts.value = assignedProducts.value.filter((id: number) => !removeIds.has(id));
        };

        const selectAllFiltered = computed({
            get: () => isAllFilteredSelected.value,
            set: (checked: boolean) => toggleSelectAllFiltered(checked)
        });

        const filteredCategories = computed(() => {
            const query = categorySearch.value.trim().toLowerCase();
            if (!query) {
                return categories.value;
            }
            return categories.value.filter((category: Record<any, any>) => String(category.label || '').toLowerCase().includes(query));
        });

        const filteredCategoryIds = computed(() => filteredCategories.value.map((category: Record<any, any>) => category.value));

        const isAllFilteredCategoriesSelected = computed(() => {
            const ids = filteredCategoryIds.value;
            if (!ids.length) {
                return false;
            }
            return ids.every((id: number) => assignedCategories.value.includes(id));
        });

        const isFilteredCategoriesIndeterminate = computed(() => {
            const ids = filteredCategoryIds.value;
            if (!ids.length) {
                return false;
            }
            const selectedCount = ids.filter((id: number) => assignedCategories.value.includes(id)).length;
            return selectedCount > 0 && selectedCount < ids.length;
        });

        const toggleSelectAllFilteredCategories = (checked: boolean | Event) => {
            const shouldSelect = typeof checked === 'boolean' ? checked : !isAllFilteredCategoriesSelected.value;
            const ids = filteredCategoryIds.value;
            if (shouldSelect) {
                assignedCategories.value = Array.from(new Set([...assignedCategories.value, ...ids]));
                return;
            }
            const removeIds = new Set(ids);
            assignedCategories.value = assignedCategories.value.filter((id: number) => !removeIds.has(id));
        };

        const selectAllFilteredCategories = computed({
            get: () => isAllFilteredCategoriesSelected.value,
            set: (checked: boolean) => toggleSelectAllFilteredCategories(checked)
        });

        // Submit Hanlder Request
        const submitHandlerForm = async () => {
            const formData = {
                product_id: assignedProducts.value,
                category_id: assignedCategories.value,
                id: product_group_id.value
            };
            loading.value = true;
            await Apollo.mutate({
                mutation: UPDATE_GROUP_PRODUCTS,
                variables: { input: formData },
                update: (store, { data: { update_group_products } }) => {
                    modal.value.hide();
                    loading.value = false;
                    Notify.success(`${i18n.t('message.RECORD_UPDATED_SUCCESSFULLY')}`);
                }
            }).catch(() => {
                loading.value = false;
            });
        };

        // Reset Form Data On Model Popup
        const resetForm = () => {
            emitter.emit('clearInput');
            current_page.value = 1;
            productSearch.value = '';
            categorySearch.value = '';
            assignedProducts.value = [];
            assignedCategories.value = [];
            editProductGroupForm.value?.resetFields();
        };

        const getProducts = () => {
            loader.value = true;
            Apollo.query({
                query: CUP_AGREEMENT_PRODUCT,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                variables: {
                    page: 1,
                    limit: 5000,
                    status: 1
                }
            }).then(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                    return;
                }

                products.value = [];
                data.products.data.forEach(element => {
                    products.value.push({
                        value: element.id,
                        label: getProductLabel(element.description?.name)
                    });
                });

                loader.value = false;
            }).catch(() => {
                loader.value = false;
            });
        };

        const getCategoryLabel = (name: string) => {
            try {
                const parsedName = JSON.parse(name);
                const locale = system_locale.value || i18n.fallbackLocale.value;
                return parsedName[locale]?.name || parsedName.en?.name || '';
            } catch {
                return name;
            }
        };

        const getCategories = () => {
            loader.value = true;
            Apollo.query({
                query: GET_CATEGORIES_DROPDOWN_LIST,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                variables: {
                    status: 1
                }
            }).then(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                    return;
                }

                categories.value = [];
                data.categories.data.forEach(element => {
                    categories.value.push({
                        value: element.id,
                        label: getCategoryLabel(element.name)
                    });
                });

                loader.value = false;
            }).catch(() => {
                loader.value = false;
            });
        };

        const getProductGroupProducts = () => {
            loader.value = true;
            Apollo.query({
                query: GET_PRODUCT_GROUP_PRODUCTS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                variables: { id: product_group_id.value }
            }).then(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                    return;
                }

                assignedProductsData.value = JSON.parse(data?.product_group_products || '[]');

                assignedProducts.value = [];
                assignedProductsData.value.forEach(element => {
                    assignedProducts.value.push(element.product_id);
                });

                loader.value = false;
            }).catch(() => {
                loader.value = false;
            });
        };

        const getProductGroupCategories = () => {
            loader.value = true;
            Apollo.query({
                query: GET_PRODUCT_GROUP_CATEGORIES,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                variables: { id: product_group_id.value }
            }).then(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                    return;
                }

                assignedCategoriesData.value = JSON.parse(data?.product_group_categories || '[]');

                assignedCategories.value = [];
                assignedCategoriesData.value.forEach(element => {
                    assignedCategories.value.push(element.category_id);
                });

                loader.value = false;
            }).catch(() => {
                loader.value = false;
            });
        };

        // Emitter To Open Model
        emitter.on('assignProductGroupAction', (product_group: Record<any, any>) => {
            resetForm();
            product_group_id.value = product_group.id;
            getProducts();
            getCategories();
            getProductGroupProducts();
            getProductGroupCategories();
            modal.value = new Modal(document.getElementById('modal_assign_product_group'));
            modal.value.show();
        });

        return {
            loading,
            editProductGroupData,
            editProductGroupForm,
            submitHandlerForm,
            products,
            categories,
            group_products,
            assignedProductsData,
            assignedCategoriesData,
            assignedProducts,
            assignedCategories,
            assignedProduct,
            productSearch,
            categorySearch,
            filteredProducts,
            filteredCategories,
            isAllFilteredSelected,
            isFilteredIndeterminate,
            selectAllFiltered,
            toggleSelectAllFiltered,
            isFilteredCategoriesIndeterminate,
            selectAllFilteredCategories,
            toggleSelectAllFilteredCategories
        };
    }
});
